import React from 'react';
import Icon from '../../../components/AppIcon';

const ValuationSummary = ({ craneData, valuationData }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  const getConfidenceColor = (score) => {
    if (score >= 85) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const getConfidenceIcon = (score) => {
    if (score >= 85) return 'CheckCircle';
    if (score >= 70) return 'AlertTriangle';
    return 'AlertCircle';
  };

  return (
    <div className="bg-surface border border-border rounded-lg shadow-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-text-primary">Valuation Summary</h2>
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-text-secondary" />
          <span className="text-sm text-text-secondary">
            {new Date()?.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </div>
      </div>
      {/* Crane Specifications */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-muted rounded-lg p-4">
          <div className="text-sm text-text-secondary mb-1">Make & Model</div>
          <div className="font-semibold text-text-primary">{craneData?.make} {craneData?.model}</div>
        </div>
        <div className="bg-muted rounded-lg p-4">
          <div className="text-sm text-text-secondary mb-1">Year</div>
          <div className="font-semibold text-text-primary">{craneData?.year}</div>
        </div>
        <div className="bg-muted rounded-lg p-4">
          <div className="text-sm text-text-secondary mb-1">Hours</div>
          <div className="font-semibold text-text-primary">{craneData?.hours?.toLocaleString()}</div>
        </div>
        <div className="bg-muted rounded-lg p-4">
          <div className="text-sm text-text-secondary mb-1">Capacity</div>
          <div className="font-semibold text-text-primary">{craneData?.capacity} tons</div>
        </div>
      </div>
      {/* Fair Market Value */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Fair Market Value Range</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-sm text-text-secondary mb-1">Low</div>
            <div className="text-2xl font-bold text-text-primary">{formatCurrency(valuationData?.fairValue?.min)}</div>
          </div>
          <div className="text-center border-l border-r border-border md:border-l md:border-r">
            <div className="text-sm text-text-secondary mb-1">Mid (Fair Value)</div>
            <div className="text-3xl font-bold text-primary">{formatCurrency(valuationData?.fairValue?.mid)}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-text-secondary mb-1">High</div>
            <div className="text-2xl font-bold text-text-primary">{formatCurrency(valuationData?.fairValue?.max)}</div>
          </div>
        </div>
      </div>
      {/* Confidence & DOM */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <Icon 
              name={getConfidenceIcon(valuationData?.confidenceScore)} 
              size={20} 
              className={getConfidenceColor(valuationData?.confidenceScore)} 
            />
            <h4 className="font-semibold text-text-primary">Confidence Score</h4>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`text-3xl font-bold ${getConfidenceColor(valuationData?.confidenceScore)}`}>
              {valuationData?.confidenceScore}%
            </div>
            <div className="flex-1">
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    valuationData?.confidenceScore >= 85 ? 'bg-success' :
                    valuationData?.confidenceScore >= 70 ? 'bg-warning' : 'bg-error'
                  }`}
                  style={{ width: `${valuationData?.confidenceScore}%` }}
                ></div>
              </div>
              <div className="text-xs text-text-secondary mt-1">
                Based on {valuationData?.comparableCount} comparable sales
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <Icon name="Clock" size={20} className="text-text-secondary" />
            <h4 className="font-semibold text-text-primary">Days on Market</h4>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-3xl font-bold text-text-primary">
              {valuationData?.daysOnMarket}
            </div>
            <div className="flex-1">
              <div className="text-sm text-text-secondary">
                Estimated selling time based on current market conditions
              </div>
              <div className="flex items-center space-x-2 mt-2">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="text-xs text-text-secondary">Active market</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValuationSummary;